/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EnvelopeClosedIcon, IdCardIcon, MobileIcon, Text, UnstyledButton } from '@/shared/ui';
import { notifications } from '@/shared/ui/components/basic/Notifications';
import { copyText } from '@/shared/utils/copy-text';
import { WhatsAppIcon } from '@/shared/ui/components/WhatsAppQRCode.parts';
import type { ClientRow } from '../../context/ClientManagementContext';

export interface ClientChannelStripProps {
  client: ClientRow;
  onEdit: () => void;
}

/** One reachable channel: what it is, what it says, and where it goes. */
interface Channel {
  id: string;
  icon: ReactNode;
  label: string;
  value: string;
  /**
   * Opened in a new tab. Only WhatsApp carries one: `tel:` and `mailto:`
   * hand the click to whatever the OS registered for the scheme, which on a
   * desktop is as often nothing at all as it is a dialer — so every other
   * channel copies its value instead, which is what the number or the
   * address was going to be used for anyway.
   */
  href?: string;
}

/**
 * The client's WhatsApp number, preferring an explicitly recorded
 * WhatsApp contact over the plain phone field.
 *
 * @param client - The client row.
 * @returns The number, or an empty string when none is reachable.
 */
function whatsAppNumber(client: ClientRow): string {
  const contact = client.contacts?.find((c) => c.type === 'whatsapp');
  return contact?.value || client.phone || '';
}

/** Phone contact types shown as their own channel, besides the main phone. */
const PHONE_TYPES = ['whatsapp', 'phone', 'phone_home', 'phone_work'] as const;

/** The strip's name for a contact type. */
function channelName(type: string, t: (key: string) => string): string {
  if (type === 'phone') return t('profile.overview.phone');
  if (type === 'email') return t('profile.overview.email');
  return t(`detail.channels.${type}`);
}

/** The channel name with the contact's own label ("Telefone · Sogra Dilma"). */
function labelled(name: string, label?: string): string {
  return label ? `${name} · ${label}` : name;
}

/** Whether two contact values name the same address or number, mask aside. */
function sameValue(a: string, b: string): boolean {
  const digits = (text: string) => text.replace(/\D/g, '');
  return a.toLowerCase() === b.toLowerCase() || (digits(a) !== '' && digits(a) === digits(b));
}

/** The label stored on the contact holding *value*, if any. */
function labelOf(client: ClientRow, value: string): string | undefined {
  return client.contacts?.find((contact) => contact.value && sameValue(contact.value, value))?.label;
}

/**
 * Every other phone or e-mail on file, one channel per contact, with its label.
 *
 * A spreadsheet import keeps every number of a cell and the name written
 * beside it ("11 99943-9453 Sogra Dilma"); showing only the first phone and
 * the first e-mail would hide the rest as if they had been lost.
 *
 * @param client - The client row.
 * @param t - The clients-namespace translation function.
 * @param kind - Which contacts: the phones, or the e-mails.
 * @param shown - Values already on the strip as the main channels.
 * @returns The channels, in contact order.
 */
function extraContactChannels(
  client: ClientRow, t: (key: string) => string, kind: 'phone' | 'email', shown: string[],
): Channel[] {
  const types: readonly string[] = kind === 'email' ? ['email'] : PHONE_TYPES;
  return (client.contacts ?? [])
    .filter((contact) => types.includes(contact.type) && contact.value)
    .filter((contact) => !shown.some((value) => value && sameValue(value, contact.value)))
    .map((contact, index) => ({
      id: `${contact.type}-${index}`,
      icon: kind === 'email'
        ? <EnvelopeClosedIcon width={15} height={15} />
        : <MobileIcon width={15} height={15} />,
      label: labelled(channelName(contact.type, t), contact.label),
      value: contact.value,
    }));
}

/**
 * The channels the strip offers, skipping the ones with no value.
 *
 * @param client - The client row.
 * @param t - The clients-namespace translation function.
 * @returns The populated channels, in reading order.
 */
function buildChannels(client: ClientRow, t: (key: string) => string): Channel[] {
  const whatsapp = whatsAppNumber(client);
  const document = client.cpf || client.cnpj || client.document || '';
  const channels: Channel[] = [];

  if (whatsapp) {
    channels.push({
      id: 'whatsapp',
      icon: <WhatsAppIcon className="h-[15px] w-[15px]" />,
      label: labelled(t('detail.channels.whatsapp'), labelOf(client, whatsapp)),
      value: whatsapp,
      href: `https://wa.me/${whatsapp.replace(/\D/g, '')}`,
    });
  }
  if (client.phone) {
    channels.push({
      id: 'phone',
      icon: <MobileIcon width={15} height={15} />,
      label: labelled(t('profile.overview.phone'), labelOf(client, client.phone)),
      value: client.phone,
    });
  }
  channels.push(...extraContactChannels(client, t, 'phone', [whatsapp, client.phone ?? '']));
  if (client.email) {
    channels.push({
      id: 'email',
      icon: <EnvelopeClosedIcon width={15} height={15} />,
      label: labelled(t('profile.overview.email'), labelOf(client, client.email)),
      value: client.email,
    });
  }
  channels.push(...extraContactChannels(client, t, 'email', [client.email ?? '']));
  if (document) {
    channels.push({
      id: 'document',
      icon: <IdCardIcon width={15} height={15} />,
      label: t('profile.overview.document'),
      value: document,
    });
  }
  return channels;
}

/** What to say after a copy attempt, either way. */
interface CopyLabels {
  copied: (label: string, value: string) => string;
  failed: string;
}


/**
 * One channel button: icon, what the channel is, and its value.
 */
function ChannelButton({ channel, labels }: { channel: Channel; labels: CopyLabels }) {
  const open = () => {
    if (channel.href) {
      window.open(channel.href, '_blank', 'noopener,noreferrer');
      return;
    }
    // A copy that silently fails is worse than one that never ran: the
    // broker walks away believing the number is on their clipboard.
    void copyText(channel.value).then((copied) => {
      notifications.show(copied
        /* Names what landed on the clipboard. "Copiado com sucesso" left
           the broker to guess which of four values they had just taken. */
        ? { type: 'success', message: labels.copied(channel.label, channel.value) }
        : { type: 'error', message: labels.failed });
    });
  };

  return (
    <UnstyledButton
      type="button"
      data-testid={`client-channel-${channel.id}`}
      onClick={open}
      className="flex min-w-0 items-center gap-(--space-2) rounded-(--radius-md) border border-solid border-(--border-subtle) bg-(--surface-primary) px-(--space-3) py-(--space-2) text-left cursor-pointer hover:bg-(--surface-hover)"
    >
      <Text component="span" className="flex flex-none text-(--seed-base)">{channel.icon}</Text>
      <Text component="span" className="flex min-w-0 flex-col">
        <Text
          component="span"
          className="text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-(--text-tertiary-soft)"
        >
          {channel.label}
        </Text>
        <Text
          component="span"
          className="truncate text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-(--text-primary)"
        >
          {channel.value}
        </Text>
      </Text>
    </UnstyledButton>
  );
}

/**
 * The band of reachable channels under the client header.
 *
 * These four values used to sit inside the overview tab. They are what a
 * broker opens the page to get at, so they come up to the top where the
 * property sheet keeps its prices.
 *
 * @param props - The client and the edit callback.
 * @returns The channel band.
 */
export function ClientChannelStrip({ client, onEdit }: ClientChannelStripProps) {
  const { t } = useTranslation('features/clients');
  const channels = buildChannels(client, t);

  return (
    <div
      data-testid="client-channel-strip"
      className="flex flex-wrap items-center gap-(--space-3) rounded-(--radius-lg) border border-(--border-subtle) bg-(--surface-card) px-(--space-4) py-(--space-3)"
    >
      {channels.map((channel) => (
        <ChannelButton
          key={channel.id}
          channel={channel}
          labels={{
            copied: (label, value) => t('detail.channels.copiedLabelled', { label, value }),
            failed: t('detail.channels.copyFailed'),
          }}
        />
      ))}
      <UnstyledButton
        type="button"
        data-testid="client-channel-edit"
        onClick={onEdit}
        /* 44px of target on a phone. The channels beside it are 55px tall
           buttons; a bare 18px link between them is the one control in the
           row somebody has to aim for. */
        className="ml-auto flex-none whitespace-nowrap border-none bg-transparent p-0 text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] text-(--seed-base) underline underline-offset-[3px] cursor-pointer max-md:min-h-11 max-md:px-(--space-2)"
      >
        {t('detail.header.editRegistration')}
      </UnstyledButton>
    </div>
  );
}
